/**
 * BreakpointContext — container-width-based responsive class resolution.
 *
 * Cogo renders templates in a sandbox (div-based, no iframe).
 * Tailwind responsive prefixes (sm:, md:, lg:) use viewport @media queries
 * which don't respond to container width. This module resolves responsive
 * classes at the JS/data level using ResizeObserver on the container element.
 *
 * Exports:
 * - BreakpointProvider: wraps app content, observes container width
 * - useContainerBreakpoint(): returns current containerWidth (Infinity = no provider)
 * - resolveResponsiveClassName(): strips/resolves Tailwind responsive prefixes
 */

import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

// Standard Tailwind min-width breakpoints
const TAILWIND_BREAKPOINTS: Record<string, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
}
const BP_ORDER = ["sm", "md", "lg", "xl", "2xl"] as const

// Matches leading responsive prefix: sm:, md:, lg:, xl:, 2xl:
const RESPONSIVE_PREFIX_RE = /^(sm|md|lg|xl|2xl):(.+)$/

// Fast check: does the string contain any responsive prefix?
const HAS_RESPONSIVE_RE = /(?:^|\s)(?:sm|md|lg|xl|2xl):/

// Viewport-anchored position values. `fixed` pins to the viewport; `sticky`
// transitions from flow to viewport-pinned. Both break the container-query
// premise: their visual geometry is determined by the viewport, not by the
// wrapper that BreakpointProvider observes. If we pre-resolve `sm:/md:/lg:`
// prefixes by container width for these elements, their responsive sizing
// desynchronises from the viewport — most visibly when an ancestor has
// padding/margin that shrinks the observed container below the breakpoint
// that the viewport still satisfies. For viewport-anchored elements we let
// Tailwind's native @media CSS do the work.
const HAS_VIEWPORT_POSITION_RE = /(?:^|\s)(?:fixed|sticky)(?:\s|$)/

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface BreakpointContextValue {
  containerWidth: number
}

const BreakpointContext = createContext<BreakpointContextValue | null>(null)

/**
 * Returns the current container width from the nearest BreakpointProvider.
 * Returns Infinity when no provider exists (backward compatible — all classes pass through).
 */
export function useContainerBreakpoint(): number {
  const ctx = useContext(BreakpointContext)
  return ctx?.containerWidth ?? Infinity
}

/**
 * Hook that returns a className resolver bound to the current container width.
 * Use in renderers that pass child.className directly to DOM elements.
 *
 * Usage: const rcls = useResolveCls(); <div className={rcls(child.className)} />
 */
export function useResolveCls(): (cls: string | undefined) => string | undefined {
  const cw = useContainerBreakpoint()
  if (cw === Infinity) return (cls) => cls
  return (cls) => cls ? resolveResponsiveClassName(cls, cw) : cls
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface BreakpointProviderProps {
  /** Ref to the element whose width should drive breakpoint resolution. */
  containerRef?: React.RefObject<HTMLElement | null>
  children: React.ReactNode
}

export function BreakpointProvider({ containerRef, children }: BreakpointProviderProps) {
  const fallbackRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(Infinity)

  useEffect(() => {
    const el = containerRef?.current ?? fallbackRef.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w =
          entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
        setWidth(w)
      }
    })
    observer.observe(el)
    // Read initial width synchronously
    setWidth(el.clientWidth)

    return () => observer.disconnect()
  }, [containerRef])

  const value = React.useMemo(() => ({ containerWidth: width }), [width])

  if (containerRef) {
    return (
      <BreakpointContext.Provider value={value}>
        {children}
      </BreakpointContext.Provider>
    )
  }

  // No external ref: wrap children in a display:contents div (no layout impact)
  return (
    <BreakpointContext.Provider value={value}>
      <div ref={fallbackRef} style={{ display: "contents" }}>
        {children}
      </div>
    </BreakpointContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Resolve Tailwind responsive classes based on container width.
 *
 * Mobile-first cascade: base → sm (640) → md (768) → lg (1024) → xl (1280) → 2xl (1536).
 * Only includes breakpoint groups where containerWidth >= breakpointWidth.
 * Uses twMerge to resolve conflicts (later breakpoint overrides earlier).
 *
 * Examples:
 *   resolveResponsiveClassName("flex flex-col sm:flex-row gap-4 sm:gap-6", 375)
 *   → "flex flex-col gap-4"
 *
 *   resolveResponsiveClassName("flex flex-col sm:flex-row gap-4 sm:gap-6", 800)
 *   → "flex flex-row gap-6"
 *
 *   resolveResponsiveClassName("hidden md:flex items-center", 375)
 *   → "hidden items-center"
 *
 *   resolveResponsiveClassName("sm:hover:bg-red-500", 800)
 *   → "hover:bg-red-500"
 */
export function resolveResponsiveClassName(
  className: string,
  containerWidth: number,
): string {
  if (!className) return ""

  // Viewport-anchored elements (position: fixed / sticky) bypass container
  // pre-resolution — their responsive sizing must track the viewport, not
  // the observed container. Tailwind's native @media queries handle it.
  if (HAS_VIEWPORT_POSITION_RE.test(className)) return className

  // Fast path: no responsive prefixes → return as-is
  if (!HAS_RESPONSIVE_RE.test(className)) return className

  const tokens = className.split(/\s+/).filter(Boolean)
  const base: string[] = []
  const byBreakpoint: Record<string, string[]> = {}

  for (const token of tokens) {
    const match = token.match(RESPONSIVE_PREFIX_RE)
    if (match) {
      const bp = match[1]
      const stripped = match[2] // rest after prefix (preserves hover:, dark:, etc.)
      if (!byBreakpoint[bp]) byBreakpoint[bp] = []
      byBreakpoint[bp].push(stripped)
    } else {
      base.push(token)
    }
  }

  // Build mobile-first cascade: base + active breakpoints in order
  const resolved = [...base]
  for (const bp of BP_ORDER) {
    if (containerWidth >= TAILWIND_BREAKPOINTS[bp] && byBreakpoint[bp]) {
      resolved.push(...byBreakpoint[bp])
    }
  }

  // twMerge resolves conflicts: flex-col + flex-row → flex-row (last wins)
  return twMerge(resolved.join(" "))
}
